'use client';

import { useMemo, useState } from 'react';
import { useLibraryStore, isPrimaryLibrary } from '@/stores/library-store';
import { useHydrated } from '@/hooks/useHydrated';
import { Header } from '@/components/layout/Header';
import { LibraryCard } from '@/components/library/LibraryCard';
import { AddLibraryDialog } from '@/components/library/AddLibraryDialog';
import { LibraryAuthDialog } from '@/components/library/LibraryAuthDialog';
import { Spinner } from '@/components/ui/Spinner';
import type { LibraryConnection } from '@/lib/opds/types';

export default function HomePage() {
  const hydrated = useHydrated();
  const libraries = useLibraryStore((s) => s.libraries);
  const [addOpen, setAddOpen] = useState(false);
  const [authTarget, setAuthTarget] = useState<LibraryConnection | null>(null);

  // Primary always renders first. The store's onRehydrateStorage hook
  // guarantees its presence; the sort here is just a stable tiebreaker
  // for any future ordering work.
  const orderedLibraries = useMemo(
    () =>
      [...libraries].sort((a, b) => {
        if (isPrimaryLibrary(a.id)) return -1;
        if (isPrimaryLibrary(b.id)) return 1;
        return a.addedAt - b.addedAt;
      }),
    [libraries]
  );

  if (!hydrated) {
    return (
      <>
        <Header title="My Libraries" />
        <main className="p-6"><Spinner className="py-16" /></main>
      </>
    );
  }

  return (
    <>
      <Header
        title="My Libraries"
        actions={
          <button
            onClick={() => setAddOpen(true)}
            className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2.5 text-base font-medium text-white hover:bg-blue-700 sm:px-3 sm:py-1.5 sm:text-sm"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add Library
          </button>
        }
      />

      <main className="p-6">
        {/* The primary library is always present (seeded by the store's
            migration + rehydrate hooks), so the empty-state branch from
            previous bookshelf revisions is gone — the user always sees
            at least the SBFLibrary card. */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {orderedLibraries.map((lib) => (
            <LibraryCard
              key={lib.id}
              library={lib}
              onEditCredentials={setAuthTarget}
            />
          ))}
        </div>
      </main>

      <AddLibraryDialog open={addOpen} onClose={() => setAddOpen(false)} />
      <LibraryAuthDialog
        library={authTarget}
        open={authTarget !== null}
        onClose={() => setAuthTarget(null)}
      />
    </>
  );
}
